import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { uiConfig, crudConfig } from '../../../../conf/criteriaResources';

// Components
import Form from '../../../../../components/Form';
import FeedbackCard from '../../../../../components/FeedbackCard';
import { Dialog } from '../../../../../components/Dialog';
import { DialogHeader } from '../../../../../components/DialogHeader';
import { DialogBody } from '../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../components/DialogFooter';
import { Button } from '../../../../../components/Button';
import { Spinner } from '../../../../../components/Spinner';
import { MaterialIcon } from '../../../../../components/MaterialIcon';
import TabbedContainer from '../../../../../components/TabbedContainer';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { ResourceParametersTable } from '../ResourceParametersTable';

// Hooks
import { useCreateCriteriaResource } from '../../../api/hooks/useCreateCriteriaResource';
import { useUpdateCriteriaResource } from '../../../api/hooks/useUpdateCriteriaResource';
import { useGetCriteriaResourceById } from '../../../api/hooks/useGetCriteriaResourceById';
import { useGetResourceTypesEnum } from '../../../api/hooks/useGetResourceTypesEnum';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

const AddEdit = ({
    formModel,
    selectedResourceId,
    closeDialog,
    sessionToken,
    saveDisabled,
    onMount,
    isExistingDoc
}) => {
    const [resourceId, setResourceId] = React.useState(selectedResourceId);
    const isNew = React.useMemo(() => resourceId === 'new', [resourceId]);
    const [wasSuccessful, setWasSuccessful] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState();
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const onClose = () => {
        if (edited) {
            setShowDiscardChangesModal(true);
        } else {
            CloseDialog();
        }
    };
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const mutationConfig = {
        crudConfig,
        sessionToken,
        onErrorCb: (e) => {
            setFatalError(e);
            setWasSuccessful(false);
        },
        onSettledCb: () => {
            setButtonClicked();
        }
    };
    const createCriteriaResource = useCreateCriteriaResource({
        ...mutationConfig,
        onSuccessCb: ({ data, context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            setResourceId(data);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const updateCriteriaResource = useUpdateCriteriaResource({
        ...mutationConfig,
        onSuccessCb: ({ context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const resourceQuery = useGetCriteriaResourceById({
        sessionToken,
        resourceId
    });
    const resourceTypesQuery = useGetResourceTypesEnum(sessionToken);
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [resourceState, setResourceState] = React.useState(null);
    React.useEffect(() => {
        if (!isNew) {
            if (resourceQuery?.isLoading) {
                setResourceState({});
                return;
            }
            if (typeof resourceQuery?.data === 'object') {
                setResourceState(resourceQuery?.data);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
                setResourceState({});
            }
            return;
        } else {
            setResourceState(typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {});
        }
    }, [isNew, resourceId, resourceQuery?.isLoading, resourceQuery?.data]);
    const updateResourceState = React.useCallback(
        (update) => {
            setResourceState({
                ...resourceState,
                ...update,
                dtoState: 'UPDATED'
            });
            setEdited(true);
        },
        [resourceState, setResourceState]
    );
    React.useEffect(() => {
        if (edited && wasSuccessful) {
            setWasSuccessful(false);
        }
    }, [edited]);

    const prepareDocument = (docState, newResource) => {
        return {
            ...docState,
            dtoState: newResource ? 'NEW' : 'UPDATED'
        };
    };

    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...resourceState,
                ...formState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc(update)) {
            setFatalError('This criteria resource already exists in the system.');
            return;
        }
        if (isNew) {
            createCriteriaResource.mutate({
                newResource: update,
                context: { closeOnSuccess: buttonClicked === 'save' }
            });
        } else {
            updateCriteriaResource.mutate({ update, context: { closeOnSuccess: buttonClicked === 'save' } });
        }
    };

    const isLoading = React.useMemo(() => createCriteriaResource.isLoading || updateCriteriaResource.isLoading, [
        createCriteriaResource,
        updateCriteriaResource
    ]);

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    const prepareOptions = () => {
        let optionsOverride = {};
        optionsOverride.resourceType = resourceTypesQuery?.data;
        return optionsOverride;
    };

    let optionsOverride = prepareOptions();

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }

    return (
        <>
            <div style={{ height: `30rem` }}>
                <Dialog close={onClose} size="lg" visible id="criteria-resource-manager-addEdit">
                    <DialogHeader close={onClose}>
                        {isNew ? 'Add Criteria Resource' : `Edit ${resourceState?.name}`}
                    </DialogHeader>
                    <DialogBody>
                        {fatalError != null && (
                            <FeedbackCard
                                intent={'danger'}
                                feedback={{
                                    heading: `An error has occurred.`,
                                    message:
                                        typeof fatalError === 'string'
                                            ? fatalError
                                            : `Please wait a few moments before trying your request
again.`
                                }}
                                details={fatalError}
                            />
                        )}
                        <TabbedContainer
                            expandHeaderX
                            tabs={{
                                details: {
                                    label: 'Resource Details',
                                    alwaysMounted: true,
                                    children: (
                                        <>
                                            {isNew ? 'Enter' : 'Adjust'} required information below.
                                            <p style={{ margin: `1.33rem 0 0 0` }}></p>
                                            {!isNew &&
                                            (resourceQuery?.isLoading ||
                                                resourceState === null ||
                                                Object.keys(resourceState).length === 0) ? (
                                                <SpinnerCont>
                                                    <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                </SpinnerCont>
                                            ) : (
                                                <Form
                                                    formModel={formModel}
                                                    isNew={isNew}
                                                    data={resourceState}
                                                    errors={errors}
                                                    compose={compose}
                                                    onApplyFormState={onApplyFormState}
                                                    setEdited={setEdited}
                                                    fatalError={fatalError}
                                                    onMount={onMount}
                                                />
                                            )}
                                        </>
                                    )
                                },
                                items: {
                                    label: 'Parameters',
                                    disabled: isNew,
                                    children: (
                                        <>
                                            {resourceQuery?.isLoading ? (
                                                <SpinnerCont>
                                                    <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                </SpinnerCont>
                                            ) : (
                                                <ResourceParametersTable
                                                    data={resourceState}
                                                    sessionToken={sessionToken}
                                                    onSave={(update) => updateResourceState(update)}
                                                />
                                            )}
                                        </>
                                    )
                                }
                            }}
                        />
                    </DialogBody>
                    <DialogFooter>
                        {isLoading && <div style={{ marginRight: '0.5rem' }}>Loading...</div>}
                        {!isLoading && wasSuccessful && (
                            <div style={{ marginRight: '0.5rem' }}>
                                <MaterialIcon icon={'done_outline'} color={'#04846D'} />
                            </div>
                        )}
                        <Button
                            text
                            appearance="primary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('save');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Save Criteria Resource
                        </Button>
                        <Button
                            text
                            appearance="secondary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('apply');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Apply
                        </Button>
                        <Button text appearance="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            CloseDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default AddEdit;

AddEdit.propTypes = {
    resourceId: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onMount: PropTypes.func,
    sessionToken: PropTypes.string,
    saveDisabled: PropTypes.bool
};
